import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * PwaUpdateBanner — surfaces a fresh deploy to installed PWA / browser users.
 *
 * index.js fires a `sw-update-available` window event when a new service
 * worker has installed and is waiting. We show a bottom banner + red dot.
 * Tapping "Update now" posts SKIP_WAITING to the waiting worker; the
 * controllerchange handler in index.js then reloads into the new build.
 */
export default function PwaUpdateBanner() {
  const [show, setShow] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const onUpdate = () => setShow(true);
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  const applyUpdate = () => {
    setApplying(true);
    const reg = window.__swReg;
    const worker = reg?.waiting || reg?.installing;
    if (worker) {
      worker.postMessage({ type: 'SKIP_WAITING' });
      // Safety: if controllerchange doesn't fire (e.g. no prior controller),
      // force a reload after a short beat.
      setTimeout(() => window.location.reload(), 1500);
    } else {
      window.location.reload();
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-[360px] z-[9999]"
      data-testid="pwa-update-banner"
    >
      <div className="relative rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700 p-4">
        {/* Red dot signalling an immediate update is required */}
        <span className="absolute -top-1.5 -left-1.5 flex h-3.5 w-3.5" data-testid="pwa-update-dot">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500" />
        </span>

        <button
          onClick={() => setShow(false)}
          className="absolute top-2.5 right-2.5 text-slate-400 hover:text-white"
          aria-label="Dismiss"
          data-testid="pwa-update-dismiss"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 pr-4">
          <div className="w-9 h-9 rounded-full bg-red-500/15 grid place-items-center shrink-0">
            <RefreshCw size={17} className="text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>App update required</p>
            <p className="text-[12px] text-slate-300 mt-0.5 leading-snug">
              A newer version of DocSite is ready. Update now for the latest features &amp; fixes.
            </p>
          </div>
        </div>

        <button
          onClick={applyUpdate}
          disabled={applying}
          className="mt-3 w-full rounded-xl py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors"
          data-testid="pwa-update-now"
        >
          <RefreshCw size={15} className={applying ? 'animate-spin' : ''} />
          {applying ? 'Updating…' : 'Update now'}
        </button>
      </div>
    </div>
  );
}
